import { useState, type FormEvent, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { api, fmt, type BankDeposit, type Payout } from '../api'
import { useSession } from '@argo/common/session'

/** Groups an IBAN in fours, the way a bank prints it. */
function spaced(iban: string): string {
  return iban.replace(/\s+/g, '').replace(/(.{4})/g, '$1 ').trim()
}

const DONE = new Set(['completed', 'rejected', 'cancelled'])
const tone = (s: string) => (s === 'completed' || s === 'credited' ? 'up' : s === 'rejected' || s === 'failed' || s === 'cancelled' ? 'down' : 'accent')

/**
 * The customer's side of the TDB bridge: where to pay in, the bank accounts
 * they pay from, and withdrawals to those accounts. Hidden entirely while the
 * bridge is off.
 */
export function BankPanels() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const { account, me } = useSession()
  const needsPin = me?.pin.set ?? false

  const info = useQuery({ queryKey: ['bank', account], queryFn: () => api.bank(account), enabled: !!account, refetchInterval: 15_000 })
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['bank', account] })
    qc.invalidateQueries({ queryKey: ['balances', account] })
    qc.invalidateQueries({ queryKey: ['ramp', account] })
  }

  const [iban, setIban] = useState('')
  const [linkNote, setLinkNote] = useState<{ text: string; kind: 'ok' | 'err' } | null>(null)
  const link = useMutation({
    mutationFn: () => api.bankLink(account, iban),
    onSuccess: () => { setIban(''); setLinkNote(null); refresh() },
    onError: (e: Error) => setLinkNote({ text: e.message, kind: 'err' }),
  })
  const unlink = useMutation({ mutationFn: (id: string) => api.bankUnlink(account, id), onSuccess: refresh })

  const verified = (info.data?.links ?? []).filter(l => l.status === 'verified')
  const [to, setTo] = useState('')
  const [amount, setAmount] = useState('')
  const [pin, setPin] = useState('')
  const [outNote, setOutNote] = useState<{ text: string; kind: 'ok' | 'err' } | null>(null)
  const payout = useMutation({
    mutationFn: () => api.bankPayout({ account, iban: to || verified[0]?.iban, amount: Number(amount), pin: pin || undefined }),
    onSuccess: () => { setAmount(''); setPin(''); setOutNote({ text: t('done'), kind: 'ok' }); refresh() },
    onError: (e: Error) => setOutNote({ text: e.message, kind: 'err' }),
  })

  const [copied, setCopied] = useState(false)
  const copy = (s: string) => {
    navigator.clipboard?.writeText(s).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500) }).catch(() => {})
  }

  if (!info.data?.enabled || !info.data.iban) return null
  const d = info.data
  const symbol = d.symbol

  const addLink = (e: FormEvent) => { e.preventDefault(); if (iban.trim()) link.mutate() }
  const askPayout = (e: FormEvent) => {
    e.preventDefault()
    setOutNote(null)
    if (!(Number(amount) > 0)) { setOutNote({ text: t('enterAmount'), kind: 'err' }); return }
    payout.mutate()
  }

  return (
    <>
      <div className="panel">
        <div className="panel-head"><span className="grow">{t('payIn')}</span><span className="chip">{d.bank}</span></div>
        <div className="panel-body">
          <p className="bank-how">{t('payInHow')}</p>
          <div className="bank-dest">
            <div className="grow">
              <div className="sub">{t('payTo')}</div>
              <div className="title num">{spaced(d.iban!)}</div>
              {d.name && <div className="sub">{t('accountHolder')}: {d.name}</div>}
            </div>
            <button type="button" className="btn-ghost" onClick={() => copy(d.iban!)}>{copied ? t('copied') : t('copy')}</button>
          </div>

          <div className="bank-sub">{t('myBankAccounts')}</div>
          {d.links.length === 0 && <p className="form-note">{t('noLinks')}</p>}
          {d.links.map(l => (
            <div className="list-row" key={l.id}>
              <div className="grow">
                <div className="title num">{spaced(l.iban)}</div>
                <div className="sub">{l.holder_name ?? ''}</div>
              </div>
              <span className={`chip ${l.status === 'verified' ? 'up' : 'accent'}`}>
                {l.status === 'verified' ? t('linkVerified') : t('linkPending')}
              </span>
              <button type="button" className="btn-ghost" disabled={unlink.isPending} onClick={() => unlink.mutate(l.id)}>{t('unlink')}</button>
            </div>
          ))}
          <form onSubmit={addLink} className="bank-link-form">
            <div className="input-wrap">
              <input aria-label="IBAN" placeholder="MN00 0000 0000 0000 0000" value={iban}
                onChange={e => setIban(e.target.value.toUpperCase())} autoComplete="off" />
            </div>
            <button className="btn-small" type="submit" disabled={link.isPending || !iban.trim()}>{t('linkIban')}</button>
          </form>
          {linkNote && <p className={`form-note ${linkNote.kind}`}>{linkNote.text}</p>}
        </div>
      </div>

      <div className="panel">
        <div className="panel-head"><span className="grow">{t('payOut')}</span></div>
        <div className="panel-body">
          <p className="bank-how">{t('payOutHow')}</p>
          {verified.length === 0 ? (
            <p className="form-note">{t('noVerified')}</p>
          ) : (
            <form onSubmit={askPayout}>
              <div className="field">
                <label htmlFor="bto">{t('payTo')}</label>
                <div className="input-wrap">
                  <select id="bto" value={to || verified[0].iban} onChange={e => setTo(e.target.value)}>
                    {verified.map(l => <option key={l.id} value={l.iban}>{spaced(l.iban)}{l.holder_name ? ` · ${l.holder_name}` : ''}</option>)}
                  </select>
                </div>
              </div>
              <div className="field">
                <label htmlFor="bamt">{t('amount')}</label>
                <div className="input-wrap">
                  <input id="bamt" inputMode="numeric" value={amount} placeholder="0"
                    onChange={e => setAmount(e.target.value.replace(/[^\d]/g, ''))} />
                  <span className="unit">{symbol}</span>
                </div>
              </div>
              {needsPin && (
                <div className="field">
                  <label htmlFor="bpin">{t('pin')}</label>
                  <div className="input-wrap">
                    <input id="bpin" type="password" inputMode="numeric" autoComplete="off" placeholder="••••"
                      value={pin} onChange={e => setPin(e.target.value.replace(/[^\d]/g, ''))} />
                  </div>
                </div>
              )}
              <button className="submit sell" type="submit" disabled={payout.isPending}>
                {payout.isPending ? t('loading') : t('requestPayout')}
              </button>
            </form>
          )}
          {outNote && <p className={`form-note ${outNote.kind}`}>{outNote.text}</p>}
        </div>
        {d.payouts.length > 0 && (
          <div className="panel-body flush">
            {d.payouts.map(p => <PayoutRow key={p.id} p={p} />)}
          </div>
        )}
      </div>
    </>
  )
}

function PayoutRow({ p, children }: { p: Payout; children?: ReactNode }) {
  const { t } = useTranslation()
  return (
    <div className="list-row">
      <span className={`chip ${tone(p.status)}`}>{t(`st_${p.status}`)}</span>
      <div className="grow">
        <div className="title num">{fmt(p.amount)} {p.symbol} → {spaced(p.iban)}</div>
        <div className="sub">
          #{p.id} · {p.account} · {new Date(p.requested_at).toLocaleString()}
          {p.bank_txn ? ` · TDB ${p.bank_txn}` : ''}
          {p.note && !DONE.has(p.status) ? ` · ${p.note}` : ''}
        </div>
      </div>
      {children}
    </div>
  )
}

/**
 * The operator's side: what the bridge would not decide alone. Deposits it
 * could not match, every withdrawal, and anything whose outcome was lost.
 * Sending money and crediting it both take the operator's own PIN.
 */
export function BankDesk() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const { me } = useSession()
  const needsPin = me?.pin.set ?? false
  const [pin, setPin] = useState('')
  const [note, setNote] = useState<{ text: string; kind: 'ok' | 'err' } | null>(null)

  const status = useQuery({ queryKey: ['bank-status'], queryFn: api.bankStatus, refetchInterval: 30_000 })
  const review = useQuery({ queryKey: ['bank-review'], queryFn: () => api.bankDeposits(), refetchInterval: 15_000 })
  const payouts = useQuery({ queryKey: ['bank-payouts'], queryFn: () => api.bankPayouts(), refetchInterval: 15_000 })

  const act = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: () => {
      setNote({ text: t('done'), kind: 'ok' })
      for (const k of ['bank-status', 'bank-review', 'bank-payouts', 'bank']) qc.invalidateQueries({ queryKey: [k] })
    },
    onError: (e: Error) => setNote({ text: e.message, kind: 'err' }),
  })
  const run = (fn: () => Promise<unknown>) => { setNote(null); act.mutate(fn) }
  const ask = (q: string, def = '') => window.prompt(q, def)?.trim() ?? null

  const s = status.data
  if (!s?.enabled) return null
  const open = (review.data?.data ?? []).filter(r => r.status === 'review' || r.status === 'crediting' || r.status === 'new')
  const waiting = (payouts.data?.data ?? []).filter(p => !DONE.has(p.status))
  const p = pin || undefined

  return (
    <div className="panel">
      <div className="panel-head">
        <span className="grow">{t('bankDesk')}</span>
        {s.sandbox && <span className="chip down">sandbox</span>}
        <button type="button" className="btn-ghost" disabled={act.isPending} onClick={() => run(api.bankPoll)}>{t('pollNow')}</button>
      </div>
      <div className="panel-body">
        <div className="bank-figures">
          <div><div className="sub">{t('bankBalance')}</div><div className="title num">{s.bank_error ? '—' : fmt(s.bank_balance ?? 0, 2)} {s.bank_currency ?? s.symbol}</div></div>
          <div><div className="sub">{t('ledgerSupply')}</div><div className="title num">{fmt(s.ledger_supply)} {s.symbol}</div></div>
        </div>
        {s.bank_error && <p className="form-note err">{s.bank_error}</p>}
        {needsPin && (
          <div className="field">
            <label htmlFor="dpin">{t('pin')}</label>
            <div className="input-wrap">
              <input id="dpin" type="password" inputMode="numeric" autoComplete="off" placeholder="••••"
                value={pin} onChange={e => setPin(e.target.value.replace(/[^\d]/g, ''))} />
            </div>
          </div>
        )}
        {note && <p className={`form-note ${note.kind}`}>{note.text}</p>}
      </div>

      <div className="bank-sub pad">{t('toReview')}</div>
      <div className="panel-body flush">
        {open.length === 0 ? <div className="empty">{t('noData')}</div> : open.map(r => (
          <DepositRow key={r.id} r={r}>
            {r.status === 'crediting' ? (
              <button type="button" className="btn-ghost" disabled={act.isPending} onClick={() => {
                const txid = ask(t('txidPrompt'))
                if (txid !== null) run(() => api.bankResolveDeposit(r.id, txid))
              }}>{t('resolve')}</button>
            ) : (
              <>
                <button type="button" className="btn-small" disabled={act.isPending} onClick={() => {
                  const to = ask(`${t('credit')} → ${t('accounts')}:`, r.account ?? r.linked_account ?? '') ?? ''
                  if (to) run(() => api.bankCredit(r.id, to, p))
                }}>{t('credit')}</button>
                <button type="button" className="btn-ghost" disabled={act.isPending} onClick={() => {
                  const why = ask(t('reasonPrompt'))
                  if (why) run(() => api.bankRejectDeposit(r.id, why))
                }}>{t('reject')}</button>
              </>
            )}
          </DepositRow>
        ))}
      </div>

      <div className="bank-sub pad">{t('awaitingPayouts')}</div>
      <div className="panel-body flush">
        {waiting.length === 0 ? <div className="empty">{t('noData')}</div> : waiting.map(x => (
          <PayoutRow key={x.id} p={x}>
            {(x.status === 'requested' || x.status === 'failed') && (
              <>
                <button type="button" className="btn-small" disabled={act.isPending}
                  onClick={() => run(() => api.bankApprove(x.id, p))}>{t('approve')}</button>
                <button type="button" className="btn-ghost" disabled={act.isPending} onClick={() => {
                  const why = ask(t('reasonPrompt'))
                  if (why) run(() => api.bankRejectPayout(x.id, why))
                }}>{t('reject')}</button>
              </>
            )}
            {(x.status === 'unknown' || x.status === 'sending') && (
              <>
                <button type="button" className="btn-small" disabled={act.isPending} onClick={() => {
                  const txn = ask(t('bankTxnPrompt'))
                  if (txn) run(() => api.bankResolvePayout(x.id, true, txn, p))
                }}>{t('markSent')}</button>
                <button type="button" className="btn-ghost" disabled={act.isPending}
                  onClick={() => run(() => api.bankResolvePayout(x.id, false, '', p))}>{t('markNotSent')}</button>
              </>
            )}
          </PayoutRow>
        ))}
      </div>
    </div>
  )
}

function DepositRow({ r, children }: { r: BankDeposit; children?: ReactNode }) {
  const { t } = useTranslation()
  const registered = [r.last_name, r.first_name].filter(Boolean).join(' ')
  const who = r.account ?? r.linked_account
  return (
    <div className="list-row">
      <span className={`chip ${tone(r.status)}`}>{t(`st_${r.status}`)}</span>
      <div className="grow">
        <div className="title num">+{fmt(r.amount, 2)} · {r.description ?? ''}</div>
        <div className="sub">
          {t('sender')}: {r.counterparty_name ?? '?'} · {r.counterparty_iban ? spaced(r.counterparty_iban) : '?'}
          {who ? ` → ${who}` : ''}{registered ? ` (${t('registeredAs')}: ${registered})` : ''}
        </div>
        {r.note && <div className="sub">{r.note}</div>}
      </div>
      {children}
    </div>
  )
}
